import { createSlice, PayloadAction } from "@reduxjs/toolkit";

const filterFieldDefaults = {
  universityId: "",
  courseType: "",
  qualificationLevelId: "",
  country: "",
};

export type CourseFilterFieldDefaults = typeof filterFieldDefaults;

const initialState = {
  search: "",
  ...filterFieldDefaults,
  appliedFilters: {} as Record<string, any>,
};

export type CourseFilterState = typeof initialState;

const courseFilterSlice = createSlice({
  name: "courseFilter",
  initialState,
  reducers: {
    setCourseSearch(state, action: PayloadAction<string>) {
      state.search = action.payload;
      state.appliedFilters = {
        ...state.appliedFilters,
        search: action.payload || undefined,
      };
      if (!action.payload) {
        delete (state.appliedFilters as any).search;
      }
    },
    setCourseFilterField(
      state,
      action: PayloadAction<{
        key: keyof CourseFilterFieldDefaults;
        value: any;
      }>
    ) {
      const { key, value } = action.payload;
      (state as any)[key] = value;
    },
    applyCourseFilters(state) {
      const applied: Record<string, any> = {};
      const current = state as Record<string, any>;
      if (state.search) applied.search = state.search;
      for (const [key, defaultVal] of Object.entries(filterFieldDefaults)) {
        if (current[key] !== defaultVal && current[key] !== "") {
          applied[key] = current[key];
        }
      }
      state.appliedFilters = applied;
    },
    clearCourseFilters() {
      return initialState;
    },
    /**
     * Programmatic preset — used when the "Show Courses" button on a
     * university card preselects a `universityId` filter before switching tabs.
     */
    setCourseAppliedFilters(
      state,
      action: PayloadAction<Partial<CourseFilterFieldDefaults & { search: string }>>
    ) {
      const next = { ...filterFieldDefaults, search: "", ...action.payload };
      Object.assign(state, next);
      const applied: Record<string, any> = {};
      if (next.search) applied.search = next.search;
      for (const [key, defaultVal] of Object.entries(filterFieldDefaults)) {
        const val = (next as any)[key];
        if (val !== defaultVal && val !== "") applied[key] = val;
      }
      state.appliedFilters = applied;
    },
  },
});

export const {
  setCourseSearch,
  setCourseFilterField,
  applyCourseFilters,
  clearCourseFilters,
  setCourseAppliedFilters,
} = courseFilterSlice.actions;

export default courseFilterSlice.reducer;
